/**
 * LEAD MOCK DATA
 *
 * Centralized mock data for leads including list views and records.
 * Uses mockDataManager for localStorage persistence.
 *
 * TO REPLACE WITH REAL API:
 * - Update query files in /src/services/queries/leadListViewQueries.ts
 * - Replace persistedLeadListViews.get() with API calls
 * - Replace persistedLeadRecords.get() with API calls
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT LEAD LIST VIEWS
 * List view configurations that define how leads are displayed
 */
export const defaultLeadListViews: ListView[] = [
  {
    id: 'lv_leads_all',
    name: 'All Leads',
    apiName: 'all_leads',
    objectApiName: 'Lead',
    description: 'All lead records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Lead ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_leads_hot',
    name: 'Hot Leads',
    apiName: 'hot_leads',
    objectApiName: 'Lead',
    description: 'Hot-rated leads',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'rating', operator: 'equals', value: 'Hot' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_leads_new',
    name: 'New Leads',
    apiName: 'new_leads',
    objectApiName: 'Lead',
    description: 'Leads with New status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'leadSource', fieldLabel: 'Lead Source', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'New' }
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_leads_qualified',
    name: 'Qualified Leads',
    apiName: 'qualified_leads',
    objectApiName: 'Lead',
    description: 'Qualified leads ready for conversion',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Lead Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'company', fieldLabel: 'Company', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'rating', fieldLabel: 'Rating', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Qualified' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT LEAD RECORDS
 * Actual lead data
 */
export const defaultLeadRecords: ListViewRecord[] = [
  {
    id: 'lead_001',
    name: 'Sarah Martinez',
    company: 'Tech Innovations Inc',
    title: 'VP of Engineering',
    email: 'sarah.martinez@techinnovations.com',
    phone: '(555) 789-0123',
    status: 'Qualified',
    leadSource: 'Website',
    rating: 'Hot',
    owner: 'John Smith',
    industry: 'Technology',
    annualRevenue: 850000,
    description: 'Interested in enterprise cloud migration solutions. Has budget allocated for Q1.',
    createdDate: '2025-01-10T09:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-15T14:30:00Z',
    lastModifiedBy: 'user_john',
    lastContact: '2025-01-15T14:30:00Z'
  },
  {
    id: 'lead_002',
    name: 'Michael Chen',
    company: 'Global Manufacturing Corp',
    title: 'Director of Operations',
    email: 'mchen@globalmanuf.com',
    phone: '(555) 890-1234',
    status: 'Contacted',
    leadSource: 'Referral',
    rating: 'Warm',
    owner: 'Sarah Johnson',
    industry: 'Manufacturing',
    annualRevenue: 2500000,
    description: 'Looking for supply chain optimization tools. Referred by existing customer.',
    createdDate: '2025-01-12T10:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-14T11:00:00Z',
    lastModifiedBy: 'user_sarah',
    lastContact: '2025-01-14T11:00:00Z'
  },
  {
    id: 'lead_003',
    name: 'Emily Rodriguez',
    company: 'Retail Solutions LLC',
    title: 'CEO',
    email: 'emily@retailsolutions.com',
    phone: '(555) 901-2345',
    status: 'New',
    leadSource: 'Trade Show',
    rating: 'Hot',
    owner: 'Michael Chen',
    industry: 'Retail',
    annualRevenue: 450000,
    description: 'Met at CRM Conference 2025. Interested in POS integration capabilities.',
    createdDate: '2025-01-15T14:00:00Z',
    createdBy: 'user_michael',
    lastModifiedDate: '2025-01-15T14:00:00Z',
    lastModifiedBy: 'user_michael'
  },
  {
    id: 'lead_004',
    name: 'David Park',
    company: 'Financial Services Group',
    title: 'Managing Director',
    email: 'dpark@finservices.com',
    phone: '(555) 012-3456',
    status: 'Contacted',
    leadSource: 'Cold Call',
    rating: 'Warm',
    owner: 'Emily Davis',
    industry: 'Finance',
    annualRevenue: 5000000,
    description: 'Needs compliance and reporting features. Currently evaluating 3 vendors.',
    createdDate: '2025-01-08T09:15:00Z',
    createdBy: 'user_emily',
    lastModifiedDate: '2025-01-13T16:45:00Z',
    lastModifiedBy: 'user_emily',
    lastContact: '2025-01-13T16:45:00Z'
  },
  {
    id: 'lead_005',
    name: 'Jennifer Taylor',
    company: 'Healthcare Innovations',
    title: 'CTO',
    email: 'jtaylor@healthcareinnovations.com',
    phone: '(555) 123-4567',
    status: 'Qualified',
    leadSource: 'Partner',
    rating: 'Hot',
    owner: 'David Wilson',
    industry: 'Healthcare',
    annualRevenue: 3200000,
    description: 'HIPAA-compliant solution required. Partner referral from AWS.',
    createdDate: '2025-01-05T11:30:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-14T10:15:00Z',
    lastModifiedBy: 'user_david',
    lastContact: '2025-01-14T10:15:00Z'
  },
  {
    id: 'lead_006',
    name: 'Robert Anderson',
    company: 'Construction Pro LLC',
    title: 'Owner',
    email: 'randerson@constructionpro.com',
    phone: '(555) 234-5678',
    status: 'Unqualified',
    leadSource: 'Website',
    rating: 'Cold',
    owner: 'Sarah Johnson',
    industry: 'Construction',
    annualRevenue: 180000,
    description: 'Budget too small for enterprise solution. May revisit in 6 months.',
    createdDate: '2024-12-28T13:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-05T09:30:00Z',
    lastModifiedBy: 'user_sarah',
    lastContact: '2025-01-05T09:30:00Z'
  },
  {
    id: 'lead_007',
    name: 'Lisa Wong',
    company: 'Education Tech Solutions',
    title: 'Founder',
    email: 'lwong@edtechsolutions.com',
    phone: '(555) 345-6789',
    status: 'New',
    leadSource: 'Referral',
    rating: 'Warm',
    owner: 'John Smith',
    industry: 'Education',
    annualRevenue: 320000,
    description: 'Startup looking for scalable CRM. Referred by venture capital partner.',
    createdDate: '2025-01-14T15:45:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-14T15:45:00Z',
    lastModifiedBy: 'user_admin'
  },
  {
    id: 'lead_008',
    name: 'James Miller',
    company: 'Logistics Central',
    title: 'VP of Sales',
    email: 'jmiller@logisticscentral.com',
    phone: '(555) 456-7890',
    status: 'Contacted',
    leadSource: 'Trade Show',
    rating: 'Warm',
    owner: 'Michael Chen',
    industry: 'Logistics',
    annualRevenue: 1800000,
    description: 'Met at Supply Chain Expo. Interested in fleet management integration.',
    createdDate: '2025-01-11T08:30:00Z',
    createdBy: 'user_michael',
    lastModifiedDate: '2025-01-15T14:00:00Z',
    lastModifiedBy: 'user_michael',
    lastContact: '2025-01-15T14:00:00Z'
  },
  {
    id: 'lead_009',
    name: 'Amanda Foster',
    company: 'Marketing Pros',
    title: 'Director of Marketing',
    email: 'afoster@marketingpros.com',
    phone: '(555) 567-8901',
    status: 'New',
    leadSource: 'Website',
    rating: 'Hot',
    owner: 'John Smith',
    industry: 'Marketing',
    annualRevenue: 650000,
    description: 'Downloaded whitepaper on marketing automation. High engagement score.',
    createdDate: '2025-01-16T09:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-16T09:00:00Z',
    lastModifiedBy: 'user_admin'
  },
  {
    id: 'lead_010',
    name: 'Christopher Lee',
    company: 'Software Dynamics',
    title: 'CIO',
    email: 'clee@softwaredynamics.com',
    phone: '(555) 678-9012',
    status: 'Qualified',
    leadSource: 'Referral',
    rating: 'Hot',
    owner: 'Emily Davis',
    industry: 'Technology',
    annualRevenue: 4200000,
    description: 'Enterprise customer with 500+ users. Replacing legacy Salesforce.',
    createdDate: '2025-01-13T11:00:00Z',
    createdBy: 'user_admin',
    lastModifiedDate: '2025-01-16T10:00:00Z',
    lastModifiedBy: 'user_emily',
    lastContact: '2025-01-16T10:00:00Z'
  }
];

/**
 * PERSISTED STORAGE
 * Creates localStorage-backed storage for list views and records
 * Changes made via UI will persist across page refreshes
 */
export const persistedLeadListViews = createPersistedMockData(
  'crm_lead_list_views',
  defaultLeadListViews
);

export const persistedLeadRecords = createPersistedMockData(
  'crm_lead_records',
  defaultLeadRecords
);

/**
 * BACKWARD COMPATIBILITY
 * Export non-persisted versions for any code that doesn't need persistence
 */
export const mockLeadListViews = defaultLeadListViews;
export const mockLeadRecords = defaultLeadRecords;
